
import { googleAuth } from './googleAuth';
import { supabase } from '../supabase';
import { Transaction, CreditCard, WealthItem } from '../types';
import { encryptValue, decryptValue, importKeyFromSession } from './crypto';

const GOOGLE_SHEETS_API_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';
const SESSION_VAULT_NAME = '_eq_vlt_blob';

export const sheetsService = {
  async checkUserStatus() {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) return { authorized: false };

    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (!profile || fetchError) {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + 15);
      const metadata = user.user_metadata || {};
      const fullName = metadata.full_name || metadata.name || user.email?.split('@')[0] || 'Usuário';

      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: user.email,
          full_name: fullName,
          subscription_status: 'trial',
          subscription_expiry: expiry.toISOString(),
          is_blocked: false,
          updated_at: new Date().toISOString()
        }, { onConflict: 'id' })
        .select()
        .single();
      
      if (insertError) return { authorized: true, id: user.id, subscription_status: 'trial', full_name: fullName, is_blocked: false };
      return { authorized: true, ...newProfile };
    }

    if (profile.is_blocked) {
      return { authorized: true, ...profile, is_blocked: true };
    }

    // Validação de Expiração em Tempo Real
    if (profile.subscription_expiry) {
      const now = new Date();
      const expiry = new Date(profile.subscription_expiry);
      
      if (now > expiry) {
        // Se expirou, retornamos o status como expired independente do que está no banco
        return { authorized: true, ...profile, subscription_status: 'expired' };
      }
    }

    return { authorized: true, ...profile };
  },

  async updateSecurityMetadata(metadata: { salt?: string, encryption_verify?: string, wrapped_key?: string }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Não autenticado");
    await supabase.from('profiles').update({
        encryption_salt: metadata.salt,
        encryption_verify: metadata.encryption_verify,
        encryption_wrapped_key: metadata.wrapped_key,
        updated_at: new Date().toISOString()
    }).eq('id', user.id);
  },

  async getMasterKey() {
    const raw = sessionStorage.getItem(SESSION_VAULT_NAME);
    if (!raw) return null;
    return await importKeyFromSession(raw);
  },

  async getOrCreateSpreadsheet() {
    const token = await googleAuth.getGoogleToken();
    if (!token) return null;
    try {
      const searchResp = await fetch(`https://www.googleapis.com/drive/v3/files?q=name='Equilibra_Soberana_DB' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const searchData = await searchResp.json();
      if (searchData.files && searchData.files.length > 0) return searchData.files[0].id;

      const createResp = await fetch(GOOGLE_SHEETS_API_BASE, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          properties: { title: 'Equilibra_Soberana_DB' },
          sheets: [{ properties: { title: 'Transactions' } }, { properties: { title: 'Cards' } }, { properties: { title: 'Wealth' } }]
        })
      });
      const newData = await createResp.json();
      if (newData.spreadsheetId) {
        await this.setupHeaders(newData.spreadsheetId, token);
        const { data: { user } } = await supabase.auth.getUser();
        if (user) await supabase.from('profiles').update({ google_sheets_id: newData.spreadsheetId }).eq('id', user.id);
        return newData.spreadsheetId;
      }
      return null;
    } catch (err) { return null; }
  },

  async setupHeaders(spreadsheetId: string, token: string) {
    const data = [
      { range: 'Transactions!A1:I1', values: [['ID', 'Data', 'Descrição', 'Valor', 'Tipo', 'Categoria', 'CardID', 'InstallmentID', 'WealthItemID']] },
      { range: 'Cards!A1:E1', values: [['ID', 'Nome', 'Limite', 'Dia Fechamento', 'Dia Vencimento']] },
      { range: 'Wealth!A1:E1', values: [['ID', 'Nome', 'Tipo', 'Valor Atual', 'Meta']] }
    ];
    for (const d of data) {
      await fetch(`${GOOGLE_SHEETS_API_BASE}/${spreadsheetId}/values/${d.range}?valueInputOption=RAW`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ values: d.values })
      });
    }
  },

  async fetchData(spreadsheetId: string) {
    const token = await googleAuth.getGoogleToken();
    const key = await this.getMasterKey();
    if (!token || !key) return null;
    try {
      const ranges = ['Transactions!A2:I', 'Cards!A2:E', 'Wealth!A2:E'];
      const resp = await fetch(`${GOOGLE_SHEETS_API_BASE}/${spreadsheetId}/values:batchGet?ranges=${ranges.join('&ranges=')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!data.valueRanges) return null;

      const transactions = (await Promise.all((data.valueRanges[0].values || []).map(async (row: any) => {
        if (!row[0]) return null; // Pula linhas vazias ou deslocadas
        return {
          id: row[0], date: row[1], 
          description: (await decryptValue(row[2], key)) || "Registro Protegido", 
          amount: parseFloat(await decryptValue(row[3], key) || "0"), 
          type: row[4], category: row[5], cardId: row[6] || '', installmentId: row[7] || '',
          wealthItemId: row[8] || ''
        };
      }))).filter(Boolean) as Transaction[];

      const cards = await Promise.all((data.valueRanges[1].values || []).map(async (row: any) => ({
        id: row[0], name: (await decryptValue(row[1], key)) || "Cartão Oculto", 
        limit: parseFloat(await decryptValue(row[2], key) || "0"), 
        closingDate: parseInt(row[3]) || 1, 
        dueDate: parseInt(row[4]) || 1,
        currentBill: 0
      })));

      const wealth = await Promise.all((data.valueRanges[2].values || []).map(async (row: any) => ({
        id: row[0], name: (await decryptValue(row[1], key)) || "Ativo Oculto", 
        type: row[2], amount: parseFloat(await decryptValue(row[3], key) || "0"), targetAmount: parseFloat(await decryptValue(row[4], key) || "0")
      })));

      return { transactions, cards, wealth };
    } catch (e) { return null; }
  },

  async syncTab(spreadsheetId: string, tab: 'Transactions' | 'Cards' | 'Wealth', rawData: any[]) {
    const token = await googleAuth.getGoogleToken();
    const key = await this.getMasterKey();
    if (!token || !key) return;

    const encryptedRows = await Promise.all(rawData.map(async (item) => {
      if (tab === 'Transactions') {
        return [item.id, item.date, await encryptValue(item.description, key), await encryptValue(String(item.amount), key), item.type, item.category, item.cardId || '', item.installmentId || '', item.wealthItemId || ''];
      } else if (tab === 'Cards') {
        return [item.id, await encryptValue(item.name, key), await encryptValue(String(item.limit), key), item.closingDate, item.dueDate];
      } else {
        return [item.id, await encryptValue(item.name, key), item.type, await encryptValue(String(item.amount), key), await encryptValue(String(item.targetAmount || 0), key)];
      }
    }));

    // Limpa um intervalo amplo para garantir que dados deslocados sejam removidos (A até Z)
    const clearRange = tab === 'Transactions' ? 'A2:Z5000' : 'A2:Z500';
    
    await fetch(`${GOOGLE_SHEETS_API_BASE}/${spreadsheetId}/values/${tab}!${clearRange}:clear`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (encryptedRows.length > 0) {
      await fetch(`${GOOGLE_SHEETS_API_BASE}/${spreadsheetId}/values/${tab}!A2?valueInputOption=RAW`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ range: `${tab}!A2`, majorDimension: 'ROWS', values: encryptedRows })
      });
    }
  },

  async saveTransactionsBatch(spreadsheetId: string, transactions: Transaction[]) {
    const key = await this.getMasterKey();
    if (!key) return;
    const encrypted = await Promise.all(transactions.map(async t => [t.id, t.date, await encryptValue(t.description, key), await encryptValue(String(t.amount), key), t.type, t.category, t.cardId || '', t.installmentId || '', t.wealthItemId || '']));
    const token = await googleAuth.getGoogleToken();
    await fetch(`${GOOGLE_SHEETS_API_BASE}/${spreadsheetId}/values/Transactions!A:I:append?valueInputOption=RAW`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ range: 'Transactions!A:I', majorDimension: 'ROWS', values: encrypted })
    });
  }
};
